/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabrielecavallo
 * File: ImportFromMpesDialog.tsx
 * Project: mle-client-n
 * Committed last: 2022/11/8 @ 1616
 * ---
 * Description:
 */

import {BaseDialogProps} from '../../../../common/props';
import classNames from 'classnames';
import {currentThemeIsDark} from '../../../../common/utils';
import {DialogStep, MultistepDialog, Spinner} from '@blueprintjs/core';
import {ImportFeature} from '../../../../store/features/mpes-io/ImportFeature';
import {Label} from '../../../../entities';
import React, {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../store';
import {UploadMpesFilePanel} from './UploadMpesFilePanel';
import {RecipeSelection, SelectRevisionPanel} from '../create-project/SelectRevisionPanel';
import {MyLabelAuthFeature} from '../../../../store/features/mylabel/auth/MyLabelAuthFeature';
import {MyLabelPanel} from '../create-project/MyLabelPanel';
import {mleDomainGqlClient} from '../../../../api/domain';
import {GET_DATAORIGINS_BY_COMPANY_ID} from '../../../../api/domain/queries.gql';
import {MleToaster, Toasts} from '../../../../common/toast';

interface Props extends BaseDialogProps {
    onImportSuccessful: (label: Label) => void;
}

export const ImportFromMpesDialog = (props: Props) => {
    const authData = useSelector((state: RootState) => state.authState);
    const [nextEnabled, setNextEnabled] = useState(false);
    const [createEnabled, setCreateEnabled] = useState(false);
    const [selection, setSelection] = useState<RecipeSelection>();
    const [myLabelIsConn, setMyLabelIsConn] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [requiresCredentials, setRequiresCredentials] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [dataOriginId, setDataOriginId] = useState<string>();

    useEffect(() => {
        (async () => {
            // Instantiate feature handy
            if (authData.user.company) {
                const connectionStatus = await MyLabelAuthFeature.checkConnection(
                    authData.user.company?.id as number);

                setMyLabelIsConn(connectionStatus);

                const credentials = !MyLabelAuthFeature.requiresCredentials();
                setRequiresCredentials(credentials);
            }
        })();
    }, [setMyLabelIsConn, authData]);

    useEffect(() => {
        setNextEnabled(false);
    }, [props.isOpen]);

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            const origins = (await mleDomainGqlClient.query({
                query: GET_DATAORIGINS_BY_COMPANY_ID,
                variables: {
                    id: authData.user.company?.id
                },
                fetchPolicy: 'no-cache'
            })).data.company.dataOrigins;

            if (origins.length)
                setDataOriginId(origins[0].id);

            setIsLoading(false);
        })();
    }, [myLabelIsConn]);

    const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const file = event.target.files[0];

            setSelectedFile(file);
        } else
            setSelectedFile(undefined);
    }, [setSelectedFile]);

    const handleImport = useCallback(async () => {
        try {
            if (selectedFile) {
                const buffer = await selectedFile.text();
                const label = await importBuffer(buffer);

                if (label)
                    props.onImportSuccessful(label);
            }

        } catch (e) {
            MleToaster.getInstance().show(Toasts.IMPORT_ERROR);
            console.error(`Impossibile importare il file ${selectedFile?.name}`);
            console.error(e);
        }
    }, [selectedFile, selection]);

    const importBuffer = async (text: string) => {
        const importer = new ImportFeature();

        return await importer.importFromMpesDocument(text, selection ? {
            activeConfig: {
                companyId: selection!.company.id,
                recipeId: selection!.revision.id
            },
            dataOriginId: dataOriginId!
        } : {
            dataOriginPlaceHolder: "-"
        });
    };

    return (
        <MultistepDialog
            className={classNames({'bp4-dark': currentThemeIsDark()})}
            isOpen={props.isOpen}
            title="Importa progetto da file"
            canOutsideClickClose={false}
            onClose={props.onClose}
            nextButtonProps={{
                disabled: !selectedFile && !nextEnabled,
                text: 'Successivo',
            }}
            backButtonProps={{text: 'Precedente'}}
            finalButtonProps={{
                text: 'Importa progetto', onClick: handleImport,
                disabled: !createEnabled,
                loading: createLoading || isLoading
            }}
        >
            {!isLoading && <DialogStep
                id="project-data"
                panel={<UploadMpesFilePanel
                    hasDataOrigin={!!dataOriginId}
                    defaultFilename={selectedFile?.name}
                    onFileChange={handleFileChange}
                />}
                title="File"
            />}
            {
                isLoading && <DialogStep
                    id="project-data"
                    panel={<Spinner size={20} className="p-6 mx-auto"/>}
                    title="File"
                />
            }
            {(!isLoading && !!dataOriginId && (!myLabelIsConn || !requiresCredentials)) &&
                <DialogStep
                    id="mylabel"
                    panel={<MyLabelPanel
                        wasConnected={myLabelIsConn}
                        setCanNext={setNextEnabled}
                    />}
                    title="Istanza Mylabel"
                />}
            {!isLoading && !!dataOriginId && myLabelIsConn &&
                <DialogStep
                    id="select-recipe"
                    title="Revisione attiva"
                    panel={<SelectRevisionPanel
                        myLabelConnected={requiresCredentials}
                        setCreateLoading={setCreateLoading}
                        setCanCreate={setCreateEnabled}
                        onSelectionChange={(value?: RecipeSelection) => {
                            setSelection(value);
                            if (value && (!value.revision || !value.company)) setCreateEnabled(
                                false);
                            else
                                setCreateEnabled(true);

                        }}
                    />}
                />}
        </MultistepDialog>
    );
};
